// Centralized initialization of all stores in the correct order
// Dependency injection through constructors for explicit dependencies

import { isEqual } from 'lodash-es';
import { reaction } from 'mobx';
import { isHydrated } from 'mobx-persist-store';

import { CartStore } from './CartStore';
import { CatalogStore } from './CatalogStore/CatalogStore';
import { CheckoutStore } from './CheckoutStore/CheckoutStore';
import { MainStore } from './MainStore';
import { OrderStore } from './OrderStore/OrderStore';
import { SettingsStore } from './Settings';
import { UserStore } from './UserStore';

export const settingStore = new SettingsStore();
export const userStore = new UserStore(settingStore);
export const catalogStore = new CatalogStore(userStore);
export const orderStore = new OrderStore(userStore);
export const checkoutStore = new CheckoutStore(userStore);
export const mainStore = new MainStore(userStore);
export const cartStore = new CartStore(
  userStore,
  orderStore,
  checkoutStore,
  mainStore,
);

export { CartStore } from './CartStore';
export { CatalogStore } from './CatalogStore/CatalogStore';
export { CheckoutStore } from './CheckoutStore/CheckoutStore';
export { MainStore } from './MainStore';
export { OrderStore } from './OrderStore/OrderStore';
export { SettingsStore } from './Settings';
export { UserStore } from './UserStore';

// MobX reactions that use multiple stores
// Must be defined after all stores are initialized

reaction(
  () => userStore.isAuthorized,
  () => {
    if (userStore.isAuthorized) {
      userStore.fetchAddresses();
    }
  },
);

reaction(
  () => userStore.isAuthorized,
  () => {
    if (userStore.isAuthorized) {
      catalogStore.notifyMeStore.getNotifyMeSKUList();
    }
  },
);

reaction(
  () => userStore.isAuthorized,
  () => {
    if (userStore.isAuthorized) {
      userStore.loyaltyPointsStore.requestTerms();
    }
  },
  { equals: isEqual },
);

reaction(
  () => userStore.loyaltyPointsStore.showLoyaltyPointsOnFrontend,
  () => {
    if (userStore.loyaltyPointsStore.showLoyaltyPointsOnFrontend) {
      userStore.loyaltyPointsStore.getLoyaltyUserBalanceAndTransactions();
    }
  },
  { equals: isEqual },
);

reaction(
  () => cartStore.calculationProcess.isLoading,
  () => {
    if (!cartStore.calculationProcess.isLoading) {
      checkoutStore.slotSelectionModalStore.setIsLoading(false);
    }
  },
);

reaction(
  () =>
    cartStore.cart.map(({ id, sku, count, selected }) => ({
      id,
      sku,
      count,
      selected,
    })),
  () => {
    if (!isHydrated(checkoutStore) || !isHydrated(cartStore)) {
      return;
    }

    checkoutStore.calculateDeliveries(checkoutStore.sortedDeliverySlotsList);
  },
  { equals: isEqual },
);

// Rebuild deliveries whenever the pool of available delivery slots changes
// (e.g. address switch to another zone). Without this, deliveries[].slot
// references stale schedule_slot_id values from the previous zone and the
// slot selector renders empty.
reaction(
  () => Object.keys(checkoutStore.deliverySlots ?? {}).join(','),
  () => {
    if (!isHydrated(checkoutStore) || !isHydrated(cartStore)) {
      return;
    }
    checkoutStore.calculateDeliveries(checkoutStore.sortedDeliverySlotsList);
  },
);

// Reactions from OrderStore
reaction(
  () => orderStore.etaCalculation?.warehouse.code,
  () => {
    if (userStore.isAuthorized) {
      catalogStore.notifyMeStore.getNotifyMeSKUList(
        orderStore.etaCalculation?.warehouse.code,
      );
    }
  },
);

reaction(
  () => [
    orderStore.etaCalculation?.warehouse.zone,
    checkoutStore.deliveryMethod,
  ],
  () => {
    const targetZone =
      orderStore.etaCalculation?.warehouse.zone[checkoutStore.deliveryMethod];

    if (targetZone) {
      mainStore.sendToRN('sendTags', {
        USER_ZONE: targetZone?.name,
      });
    }
  },
);

// Availability changed — slots might already be stale for existing
// deliveries. Drop expired slot references and report the change, but
// do NOT refetch ETA here: setEtaCalculation has just written fresh
// data, and calling reCalculateDeliveries would trigger a redundant
// requestETA round-trip that would itself write etaCalculation a
// second time, causing a duplicate /calculate refetch.
reaction(
  () =>
    Object.values(
      orderStore.etaCalculation?.warehouse.availability.deliveryMechanics ?? {},
    ).map((i) => i.availableNow),
  () => {
    if (!isHydrated(checkoutStore) || !isHydrated(userStore)) {
      return;
    }

    checkoutStore.checkSlotExpirationOfExistingDeliveries(
      checkoutStore.sortedDeliverySlotsList,
    );
    checkoutStore.slotsChangedEventStore.checkDifferenceWithPreviousDeliveries();
  },
  { equals: isEqual },
);

// Reactions from CheckoutStore
reaction(
  () => checkoutStore.isDeliverTogether,
  () => {
    if (!isHydrated(checkoutStore) || !isHydrated(cartStore)) {
      return;
    }

    checkoutStore.calculateDeliveries(checkoutStore.sortedDeliverySlotsList);
  },
);

reaction(
  () => checkoutStore.deliveryMethod,
  async () => {
    try {
      if (
        !isHydrated(checkoutStore) ||
        !isHydrated(userStore) ||
        orderStore.isETALoading
      ) {
        return;
      }

      await orderStore.requestETA();
      checkoutStore.calculateDeliveries(checkoutStore.sortedDeliverySlotsList);
    } finally {
      checkoutStore.deliveryMethodChangeMeta.setLoading(false);
    }
  },
);
